import {
  useMutation,
  useQuery,
  useQueryClient,
  keepPreviousData,
  type UseMutationOptions,
} from '@tanstack/react-query';
import { isAxiosError } from 'axios';
import { dataCentralApi } from '@/api/client';

/**
 * Affiliate / referral program data layer. All endpoints live on the v4
 * DataCentral backend (`/partner/affiliate/*`) — the current and only version.
 * Shapes are kept defensive: the live API returns a few fields the legacy TS
 * types didn't list (e.g. stats `pending_commission` / `paid_commission`).
 */

export interface AffiliateProfile {
  affiliate_id: number;
  referrer_id: number;
  customer_id?: number;
  referral_code: string;
  referral_link: string;
  tier: string;
  commission_percent: number;
  payout_threshold: number;
  payout_method?: string;
  payout_details?: Record<string, unknown>;
  status: string;
  total_clicks: number;
  total_signups: number;
  total_conversions: number;
  total_revenue: number;
  total_commission: number;
  unpaid_commission: number;
  referrer_name?: string;
  referrer_email?: string;
  referrer_phone?: string;
  referrer_company?: string;
  created_at: string;
  updated_at: string;
}

export interface AffiliateStats {
  total_clicks: number;
  unique_visitors?: number;
  total_signups: number;
  total_conversions: number;
  conversion_rate: number;
  total_revenue: number;
  total_commission: number;
  pending_commission?: number;
  paid_commission?: number;
  avg_order_value?: number;
}

export interface AffiliateDailyStats {
  date: string;
  clicks: number;
  conversions: number;
  // Not yet returned by the backend (daily endpoint is clicks/visitors/conversions only).
  signups?: number;
  revenue?: number;
  commission?: number;
}

export interface AffiliateClick {
  click_id: number;
  promo_code?: string;
  referer_url?: string;
  landing_url?: string;
  utm_source?: string;
  utm_campaign?: string;
  converted: boolean;
  converted_at?: string;
  clicked_at: string;
}

export interface AffiliateConversion {
  click_id: number;
  customer_id: number;
  customer_name?: string;
  customer_email?: string;
  converted_at: string;
  customer_created_at?: string;
  // Per-conversion commission aggregates from the backend affiliate_commissions join.
  // A converted signup with no invoice yet arrives with zeros / empty status.
  total_revenue?: number;
  total_commission?: number;
  commission_count?: number;
  commission_status?: string;
  currency?: string; // backend omits today → formatCurrency defaults to VND
}

export interface AffiliatePayout {
  payout_id: number;
  amount: number;
  currency: string;
  period_start: string;
  period_end: string;
  payout_method?: string;
  status: string;
  notes?: string;
  rejection_reason?: string;
  requested_at: string;
  approved_at?: string;
  processed_at?: string;
}

export interface Paginated<T> {
  data: T[];
  total: number;
  page: number;
  page_size: number;
}

export interface RegisterAffiliateRequest {
  name: string;
  email: string;
  phone?: string;
  company?: string;
  referral_code?: string;
  payout_method?: string;
}

export interface UpdateAffiliateRequest {
  payout_method?: string;
  payout_details?: Record<string, unknown>;
  payout_threshold?: number;
}

export interface RequestPayoutRequest {
  amount?: number;
  notes?: string;
}

export const affiliateKeys = {
  all: ['affiliate'] as const,
  profile: ['affiliate', 'profile'] as const,
  referralLink: ['affiliate', 'referral-link'] as const,
  stats: ['affiliate', 'stats'] as const,
  daily: (days?: number) => ['affiliate', 'daily', days] as const,
  clicks: (page: number, pageSize: number) => ['affiliate', 'clicks', page, pageSize] as const,
  conversions: (page: number, pageSize: number) =>
    ['affiliate', 'conversions', page, pageSize] as const,
  payouts: (page: number, pageSize: number) => ['affiliate', 'payouts', page, pageSize] as const,
};

function result<T>(data: unknown): T {
  const d = data as { result?: T; data?: T };
  return (d?.result ?? d?.data) as T;
}

/** Affiliate profile, or null if the user hasn't joined the program yet (404). */
export function useAffiliateProfile() {
  return useQuery<AffiliateProfile | null>({
    queryKey: affiliateKeys.profile,
    queryFn: async () => {
      try {
        const res = await dataCentralApi.get('/partner/affiliate/profile');
        return result<AffiliateProfile>(res.data) ?? null;
      } catch (e) {
        if (isAxiosError(e) && e.response?.status === 404) return null;
        throw e;
      }
    },
    staleTime: 60_000,
    retry: 1,
  });
}

export function useAffiliateStats(enabled = true) {
  return useQuery<AffiliateStats | null>({
    queryKey: affiliateKeys.stats,
    enabled,
    queryFn: async () => {
      const res = await dataCentralApi.get('/partner/affiliate/stats');
      return result<AffiliateStats>(res.data) ?? null;
    },
    staleTime: 60_000,
    retry: 1,
  });
}

export function useAffiliateDailyStats(days?: number, enabled = true) {
  return useQuery<AffiliateDailyStats[]>({
    queryKey: affiliateKeys.daily(days),
    enabled,
    queryFn: async () => {
      const res = await dataCentralApi.get('/partner/affiliate/stats/daily', {
        params: { days: days || undefined },
      });
      return result<AffiliateDailyStats[]>(res.data) ?? [];
    },
    staleTime: 60_000,
  });
}

function paginatedQuery<T>(
  path: string,
  key: readonly unknown[],
  page: number,
  pageSize: number,
  enabled: boolean,
) {
  return {
    queryKey: key,
    enabled,
    placeholderData: keepPreviousData,
    queryFn: async (): Promise<Paginated<T>> => {
      const res = await dataCentralApi.get(path, {
        params: { limit: pageSize, offset: (page - 1) * pageSize },
      });
      const r = result<Paginated<T>>(res.data);
      return {
        data: r?.data ?? [],
        total: r?.total ?? 0,
        page,
        page_size: pageSize,
      };
    },
    staleTime: 60_000,
  };
}

export function useAffiliateClicks(page = 1, pageSize = 20, enabled = true) {
  return useQuery(
    paginatedQuery<AffiliateClick>(
      '/partner/affiliate/clicks',
      affiliateKeys.clicks(page, pageSize),
      page,
      pageSize,
      enabled,
    ),
  );
}

export function useAffiliateConversions(page = 1, pageSize = 20, enabled = true) {
  return useQuery(
    paginatedQuery<AffiliateConversion>(
      '/partner/affiliate/conversions',
      affiliateKeys.conversions(page, pageSize),
      page,
      pageSize,
      enabled,
    ),
  );
}

export function useAffiliatePayouts(page = 1, pageSize = 20, enabled = true) {
  return useQuery(
    paginatedQuery<AffiliatePayout>(
      '/partner/affiliate/payouts',
      affiliateKeys.payouts(page, pageSize),
      page,
      pageSize,
      enabled,
    ),
  );
}

// ---- Mutations ----

function useAffiliateInvalidation() {
  const qc = useQueryClient();
  return () => qc.invalidateQueries({ queryKey: affiliateKeys.all });
}

export function useRegisterAffiliate(
  options?: UseMutationOptions<AffiliateProfile, unknown, RegisterAffiliateRequest>,
) {
  const invalidate = useAffiliateInvalidation();
  return useMutation({
    mutationFn: async (body: RegisterAffiliateRequest) => {
      const res = await dataCentralApi.post('/partner/affiliate/register', body);
      return result<AffiliateProfile>(res.data);
    },
    ...options,
    onSuccess: (...args) => {
      invalidate();
      options?.onSuccess?.(...args);
    },
  });
}

export function useUpdateAffiliateProfile(
  options?: UseMutationOptions<AffiliateProfile, unknown, UpdateAffiliateRequest>,
) {
  const invalidate = useAffiliateInvalidation();
  return useMutation({
    mutationFn: async (body: UpdateAffiliateRequest) => {
      const res = await dataCentralApi.put('/partner/affiliate/profile', body);
      return result<AffiliateProfile>(res.data);
    },
    ...options,
    onSuccess: (...args) => {
      invalidate();
      options?.onSuccess?.(...args);
    },
  });
}

export function useRequestPayout(
  options?: UseMutationOptions<AffiliatePayout, unknown, RequestPayoutRequest>,
) {
  const invalidate = useAffiliateInvalidation();
  return useMutation({
    mutationFn: async (body: RequestPayoutRequest) => {
      const res = await dataCentralApi.post('/partner/affiliate/payouts', body);
      return result<AffiliatePayout>(res.data);
    },
    ...options,
    onSuccess: (...args) => {
      invalidate();
      options?.onSuccess?.(...args);
    },
  });
}

// ---- Shared formatting helpers ----

export function formatCurrency(
  amount: number | null | undefined,
  currency = 'VND',
): string {
  if (amount == null || !Number.isFinite(amount)) return '—';
  try {
    return new Intl.NumberFormat(currency === 'VND' ? 'vi-VN' : 'en-US', {
      style: 'currency',
      currency: currency || 'VND',
      maximumFractionDigits: currency === 'VND' ? 0 : 2,
    }).format(amount);
  } catch {
    return `${amount.toLocaleString()} ${currency}`;
  }
}

export function statusTone(
  status?: string,
): 'success' | 'warning' | 'danger' | 'neutral' {
  const s = (status ?? '').toLowerCase();
  if (['active', 'completed', 'approved', 'paid', 'confirmed'].includes(s)) return 'success';
  if (['pending', 'processing'].includes(s)) return 'warning';
  if (['suspended', 'terminated', 'cancelled', 'failed', 'rejected', 'refunded'].includes(s)) return 'danger';
  return 'neutral';
}
